// Stimulus controller for making records sortable using Sortable.js.
//
// Usage:
//
// 1. Setup:
//    - Add `data-controller="kegs--sorting"` to the container of sortable items.
//    - Each sortable item must have a `data-sortable-update-url` attribute containing the URL to update its position on the server.
//
// 2. Options (via values):
//    - `resource-name-value`: Optional. The resource name for parameter nesting (e.g., "task" for `task[position]`).
//    - `param-name-value`: Optional. Defaults to "position". The name of the position parameter sent to the server.
//    - `response-kind-value`: Optional. Defaults to "html". The expected response format for the Rails request.
//
// 3. Requirements:
//    - Each sortable item must have a drag handle with class `.sort-handle`.
//    - The server must be set up to accept PATCH requests to update the position.
//
// Behavior:
//
// - When an item is reordered, a PATCH request is sent to the `sortable-update-url` with the new position (1-based index).
// - If nested sortable lists are used, the controller uses a workaround to support them properly (see reference).
//
// References:
// - https://www.stimulus-components.com/docs/stimulus-sortable
// - Fix for nested sortables: https://github.com/stimulus-components/stimulus-sortable/pull/28/files

import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { patch } from "@rails/request.js"

export default class extends Controller {
  static values = {
    resourceName: String,
    paramName: {
      type: String,
      default: "position",
    },
    responseKind: {
      type: String,
      default: "html",
    },
  }

  initialize() {
    this.onUpdate = this.onUpdate.bind(this)
  }

  connect() {
    if (Sortable.active) {
      this.sortable = Sortable.get(this.element)
    } else {
      this.sortable = new Sortable(this.element, {...this.options})
    }
  }

  disconnect() {
    if (!Sortable.active) {
      this.sortable.destroy()
    }
    this.sortable = undefined
  }

  async onUpdate({ item, newIndex }) {
    if (!item.dataset.sortableUpdateUrl) return

    const param = this.resourceNameValue ? `${this.resourceNameValue}[${this.paramNameValue}]` : this.paramNameValue

    const data = new FormData()
    data.append(param, newIndex + 1)

    return await patch(item.dataset.sortableUpdateUrl, { body: data, responseKind: this.responseKindValue })
  }

  get options() {
    return {
      animation: 0,
      handle: ".sort-handle",
      onUpdate: this.onUpdate,
    }
  }
}
